"use client";

import { cva, type VariantProps } from "class-variance-authority";
import { Check, Loader2, Upload, X } from "lucide-react";
import type * as React from "react";
import { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import {
  formatFileSize,
  getFileIcon,
  validateFile,
} from "../../lib/file-utils";
import { cn } from "../../lib/utils";
import { Button } from "../ui/button";
import { Progress } from "../ui/progress";

// CONTAINER STYLING (for wrapper with title/description)
const fileUploaderContainerVariants = cva(
  "group relative flex w-full flex-col gap-2 transition-all duration-200",
  {
    variants: {
      variant: {
        card: [
          "rounded-lg border bg-card p-4 text-card-foreground shadow-sm",
          "hover:shadow-md",
        ],
        outline: ["rounded-lg border-2 p-4", "hover:border-primary/50"],
        ghost: ["rounded-lg p-4 hover:bg-muted/50"],
        minimal: "p-2",
      },
      size: {
        sm: "gap-1",
        md: "gap-2",
        lg: "gap-3",
      },
    },
    defaultVariants: {
      variant: "card",
      size: "md",
    },
  }
);

// DROPZONE STYLING
const dropzoneVariants = cva(
  "relative flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed transition-colors",
  {
    variants: {
      variant: {
        default: "border-border bg-transparent hover:bg-muted/50",
        dropzone: "border-border bg-muted/20 hover:bg-muted/50",
        button: "border-transparent bg-transparent",
        card: "border-border bg-transparent",
      },
      size: {
        sm: "min-h-[120px] p-4",
        md: "min-h-[160px] p-6",
        lg: "min-h-[200px] p-8",
      },
      isDragActive: {
        true: "border-primary bg-primary/5",
        false: "",
      },
      disabled: {
        true: "cursor-not-allowed opacity-50 hover:bg-transparent",
        false: "",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "md",
      isDragActive: false,
      disabled: false,
    },
  }
);

// FILE PREVIEW STYLING
const filePreviewVariants = cva(
  "relative rounded-lg border bg-muted/30 p-3 transition-colors",
  {
    variants: {
      status: {
        idle: "",
        uploading: "border-primary/50",
        success: "border-green-500/50 bg-green-500/5",
        error: "border-destructive/50 bg-destructive/5",
      },
    },
    defaultVariants: {
      status: "idle",
    },
  }
);

// MAIN PROPS INTERFACE
export interface FileUploaderItemProps
  extends VariantProps<typeof fileUploaderContainerVariants> {
  // Validation
  accept?: string;

  // Features
  allowDragDrop?: boolean;

  // Accessibility
  ariaLabel?: string;
  className?: string;
  classNames?: {
    container?: string;
    title?: string;
    description?: string;
    dropzone?: string;
    preview?: string;
  };
  containerSize?: "sm" | "md" | "lg";
  containerVariant?: "card" | "outline" | "ghost" | "minimal";
  description?: string;

  // State
  disabled?: boolean;
  dropzoneSize?: "sm" | "md" | "lg";

  // Styling
  dropzoneVariant?: "default" | "dropzone" | "button" | "card";
  error?: string;
  // Core
  id?: string | number;
  label?: string;
  maxFiles?: number;
  maxSize?: number;
  multiple?: boolean;
  onError?: (error: string) => void;
  onRemove?: (file: File) => void;

  // Callbacks
  onUpload?: (files: File[]) => void | Promise<void>;
  required?: boolean;
  showContainer?: boolean;
  showPreview?: boolean;
  showProgress?: boolean;
  title?: string;
}

export function FileUploaderItem({
  label = "Upload files",
  description,
  title,
  id: _id,
  accept,
  maxSize,
  maxFiles = 1,
  multiple = false,
  disabled = false,
  required: _required = false,
  error: externalError,
  allowDragDrop = true,
  showPreview = true,
  showProgress = false,
  onUpload,
  onRemove,
  onError,
  dropzoneVariant = "default",
  dropzoneSize = "md",
  showContainer = false,
  containerVariant = "card",
  containerSize = "md",
  className,
  classNames,
  ariaLabel,
}: FileUploaderItemProps): React.ReactElement {
  const [files, setFiles] = useState<File[]>([]);
  const [internalError, setInternalError] = useState<string | undefined>();
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>(
    {}
  );
  const [uploadStatus, setUploadStatus] = useState<
    Record<string, "uploading" | "success" | "error">
  >({});

  const error = externalError ?? internalError;

  const handleFiles = useCallback(
    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: Handles file validation, upload progress tracking, error states, and async upload with multiple conditional paths
    async (newFiles: File[]) => {
      setInternalError(undefined);

      // Validate max files
      const totalFiles = files.length + newFiles.length;
      if (totalFiles > maxFiles) {
        const errorMsg = `Maximum ${maxFiles} file${maxFiles > 1 ? "s" : ""} allowed`;
        setInternalError(errorMsg);
        onError?.(errorMsg);
        return;
      }

      // Validate each file
      const validFiles: File[] = [];
      for (const file of newFiles) {
        const validationError = validateFile(file, { accept, maxSize });
        if (validationError) {
          setInternalError(validationError);
          onError?.(validationError);
          return;
        }
        validFiles.push(file);
      }

      // Add files to state
      setFiles((prev) => [...prev, ...validFiles]);

      // Call onUpload callback
      if (onUpload) {
        // Simulate upload progress if showProgress is enabled
        if (showProgress) {
          for (const file of validFiles) {
            const fileKey = `${file.name}-${file.lastModified}`;
            setUploadStatus((prev) => ({ ...prev, [fileKey]: "uploading" }));

            // Simulate progress
            for (let progress = 0; progress <= 100; progress += 10) {
              await new Promise((resolve) => {
                setTimeout(resolve, 100);
              });
              setUploadProgress((prev) => ({ ...prev, [fileKey]: progress }));
            }

            setUploadStatus((prev) => ({ ...prev, [fileKey]: "success" }));
          }
        }

        try {
          await onUpload(validFiles);
        } catch (err) {
          const errorMsg = err instanceof Error ? err.message : "Upload failed";
          setInternalError(errorMsg);
          onError?.(errorMsg);

          // Mark all files as error
          for (const file of validFiles) {
            const fileKey = `${file.name}-${file.lastModified}`;
            setUploadStatus((prev) => ({ ...prev, [fileKey]: "error" }));
          }
        }
      }
    },
    [files.length, maxFiles, accept, maxSize, onUpload, onError, showProgress]
  );

  const handleRemoveFile = useCallback(
    (fileToRemove: File) => {
      setFiles((prev) => prev.filter((f) => f !== fileToRemove));
      onRemove?.(fileToRemove);

      // Clear progress and status
      const fileKey = `${fileToRemove.name}-${fileToRemove.lastModified}`;
      setUploadProgress((prev) => {
        const newProgress = { ...prev };
        delete newProgress[fileKey];
        return newProgress;
      });
      setUploadStatus((prev) => {
        const newStatus = { ...prev };
        delete newStatus[fileKey];
        return newStatus;
      });
    },
    [onRemove]
  );

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      handleFiles(acceptedFiles);
    },
    [handleFiles]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: accept
      ? accept.split(",").reduce(
          (acc, type) => {
            const trimmedType = type.trim();
            if (trimmedType.startsWith(".")) {
              // File extension
              acc[`application/${trimmedType.slice(1)}`] = [trimmedType];
            } else if (trimmedType.includes("/*")) {
              // Wildcard MIME type
              acc[trimmedType] = [];
            } else {
              // Specific MIME type
              acc[trimmedType] = [];
            }
            return acc;
          },
          {} as Record<string, string[]>
        )
      : undefined,
    maxSize,
    multiple: multiple || maxFiles > 1,
    disabled,
    noClick: !allowDragDrop, // If drag & drop is disabled, use the file input instead
    noKeyboard: !allowDragDrop,
  });

  // Build dropzone content
  const dropzoneContent = (
    // biome-ignore lint/a11y/useSemanticElements: Dropzone needs to be a div for drag-drop functionality from react-dropzone
    <div
      {...(allowDragDrop ? getRootProps() : {})}
      aria-invalid={!!error}
      aria-label={ariaLabel ?? label}
      className={cn(
        dropzoneVariants({
          variant: dropzoneVariant,
          size: dropzoneSize,
          isDragActive,
          disabled,
        }),
        classNames?.dropzone,
        !(showContainer || showPreview) && className
      )}
      role="button"
      tabIndex={0}
    >
      <input {...getInputProps()} />

      {dropzoneVariant === "button" ? (
        <Button disabled={disabled} type="button" variant="outline">
          <Upload className="mr-2 size-4" />
          {label}
        </Button>
      ) : (
        <>
          <div className="mb-2 rounded-full bg-muted p-3">
            <Upload className="h-5 w-5 text-muted-foreground" />
          </div>
          <p className="font-medium text-foreground text-sm">
            {isDragActive ? "Drop files here" : label}
          </p>
          {description && (
            <p className="mt-1 text-center text-muted-foreground text-sm">
              {description}
            </p>
          )}
          {(maxSize || accept) && (
            <p className="mt-2 text-center text-muted-foreground text-xs">
              {maxSize && `Max size: ${formatFileSize(maxSize)}`}
              {maxSize && accept && " • "}
              {accept && `Accepted: ${accept}`}
            </p>
          )}
        </>
      )}
    </div>
  );

  // File preview list
  const filePreviewList = showPreview && files.length > 0 && (
    <div className="mt-4 space-y-2">
      {files.map((file) => {
        const fileKey = `${file.name}-${file.lastModified}`;
        const progress = uploadProgress[fileKey] ?? 0;
        const status = uploadStatus[fileKey] ?? "idle";
        const FileIcon = getFileIcon(file);

        return (
          <div
            className={cn(filePreviewVariants({ status }), classNames?.preview)}
            key={fileKey}
          >
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-sm bg-background shadow-sm ring-1 ring-input ring-inset">
                <FileIcon className="size-5 text-foreground" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-foreground text-sm">
                  {file.name}
                </p>
                <p className="flex items-center gap-2 text-muted-foreground text-xs">
                  <span>{formatFileSize(file.size)}</span>
                  {status === "uploading" && (
                    <span className="flex items-center gap-1">
                      <Loader2 className="size-3 animate-spin" />
                      {progress}%
                    </span>
                  )}
                  {status === "success" && (
                    <span className="flex items-center gap-1 text-green-600">
                      <Check className="size-3" />
                      Complete
                    </span>
                  )}
                  {status === "error" && (
                    <span className="text-destructive">Failed</span>
                  )}
                </p>
                {showProgress && status === "uploading" && (
                  <Progress className="mt-1 h-1" value={progress} />
                )}
              </div>
              <Button
                aria-label={`Remove ${file.name}`}
                className="h-8 w-8 shrink-0 p-0"
                disabled={status === "uploading"}
                onClick={() => handleRemoveFile(file)}
                size="sm"
                type="button"
                variant="ghost"
              >
                <X className="size-4" />
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );

  // Error message
  const errorMessage = error && (
    <p className="mt-2 text-destructive text-sm" role="alert">
      {error}
    </p>
  );

  // If no container, return dropzone and preview directly
  if (!showContainer) {
    return (
      <div className={cn("w-full", className)}>
        {dropzoneContent}
        {filePreviewList}
        {errorMessage}
      </div>
    );
  }

  // Render with container wrapper
  return (
    <div
      className={cn(
        fileUploaderContainerVariants({
          variant: containerVariant,
          size: containerSize,
        }),
        classNames?.container,
        className
      )}
    >
      {title && (
        <div className={cn("font-semibold tracking-tight", classNames?.title)}>
          {title}
        </div>
      )}
      {dropzoneContent}
      {filePreviewList}
      {errorMessage}
    </div>
  );
}

// COLLECTION COMPONENT
export interface FileUploaderCollectionProps
  extends VariantProps<typeof fileUploaderContainerVariants> {
  allowDragDrop?: boolean;

  // Styling
  className?: string;
  columns?: 1 | 2 | 3 | 4;
  dropzoneSize?: "sm" | "md" | "lg";

  // Shared styling (can be overridden per item)
  dropzoneVariant?: "default" | "dropzone" | "button" | "card";
  gap?: "sm" | "md" | "lg";
  items: FileUploaderItemProps[];

  // Layout
  layout?: "stack" | "grid" | "inline";

  // Callbacks
  onItemUpload?: (
    item: FileUploaderItemProps,
    files: File[],
    index: number
  ) => void;
  showPreview?: boolean;
  showProgress?: boolean;
}

const fileUploaderCollectionVariants = cva("w-full", {
  variants: {
    layout: {
      stack: "flex flex-col",
      grid: "grid",
      inline: "flex flex-row flex-wrap",
    },
    columns: {
      1: "grid-cols-1",
      2: "grid-cols-1 sm:grid-cols-2",
      3: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
      4: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4",
    },
    gap: {
      sm: "gap-2",
      md: "gap-4",
      lg: "gap-6",
    },
  },
  defaultVariants: {
    layout: "stack",
    gap: "md",
  },
});

export function FileUploaderCollection({
  items,
  layout = "stack",
  columns = 1,
  gap = "md",
  dropzoneVariant = "default",
  dropzoneSize = "md",
  showPreview = true,
  showProgress = false,
  allowDragDrop = true,
  onItemUpload,
  className,
}: FileUploaderCollectionProps): React.ReactElement {
  return (
    <div
      className={cn(
        fileUploaderCollectionVariants({
          layout,
          columns: layout === "grid" ? columns : undefined,
          gap,
        }),
        className
      )}
    >
      {items.map((item, index) => (
        <FileUploaderItem
          allowDragDrop={item.allowDragDrop ?? allowDragDrop}
          dropzoneSize={item.dropzoneSize ?? dropzoneSize}
          dropzoneVariant={item.dropzoneVariant ?? dropzoneVariant}
          key={item.id ?? item.title ?? `file-uploader-${index}`}
          onUpload={
            onItemUpload
              ? (files) => onItemUpload(item, files, index)
              : item.onUpload
          }
          showPreview={item.showPreview ?? showPreview}
          showProgress={item.showProgress ?? showProgress}
          {...item}
        />
      ))}
    </div>
  );
}
